import React from "react";
import {
  Building2,
  ShieldCheck,
  Gauge,
  Layers,
  AlertTriangle,
  Activity,
  Info,
  CheckCircle2,
  CircleDot,
  XCircle,
  ShieldAlert,
  TrendingDown,
  Bug,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { safeRatioPct, safeFixed } from "@/lib/format";
import { useTranslation } from "@/lib/i18n";

/**
 * InstitutionalSnapshot — premium compact panel that surfaces the
 * deterministic Phase-1 + Phase-2 institutional engine output without
 * overwhelming the reader.
 *
 * Phase 2 additions surface inside the SAME panel as Phase 1:
 *   • Hard/soft veto banner (when active)
 *   • Value-trap warning (when active)
 *   • Confidence % shown in the Confidence tile
 *   • "Top detected risks" compact list (top 3-4 risks across categories)
 *
 * Design intent:
 *   • Beginner-friendly — every label has a hover/tap tooltip in plain English
 *   • Compact — fits above the chart and stacks cleanly on mobile (2-col → 3-col)
 *   • Premium — same tile/border idiom as KeyDriversPanel for visual cohesion
 *   • Safe — silently renders nothing when `institutional.available !== true`
 */
export default function InstitutionalSnapshot({ institutional, valuation }) {
  const { t, locale } = useTranslation();
  if (!institutional || institutional.available !== true) return null;

  const cls = institutional.classification || {};
  const gate = institutional.quality_gate || {};
  const conf = institutional.confidence || {};
  const risk = institutional.risk || {};
  const val = institutional.valuation || {};
  const methods = institutional.valuation_methods || [];
  const breakdown = institutional.score_breakdown || {};
  const reasoning = institutional.reasoning || [];
  const hardVetoes = institutional.hard_vetoes || [];
  const softVetoes = institutional.soft_vetoes || [];
  const valueTrap = institutional.value_trap_flag || {};
  const detectedRisks = institutional.detected_risks || [];
  const confidenceScore = institutional.confidence_score ?? conf.score;
  const confidenceLabel = institutional.confidence_label ?? conf.label;

  // Helper: t() returns the key itself when no translation is found, so we
  // need an explicit "did we get a real translation?" check before falling
  // back to the backend-provided label.
  const tx = (key, vars) => {
    const v = t(key, vars);
    return v === key ? null : v;
  };

  // Phase 7.UI.13b — When DE is selected, reconstruct the deterministic
  // "How we got there" bullets client-side from the structured data
  // already exposed by the backend. This keeps the backend templates and
  // cache unchanged (English-only) while ensuring German UI is fully
  // localized. For EN we use the backend strings as-is.
  const localizedReasoning = (() => {
    if (locale !== "de") return reasoning;
    const out = [];
    if (cls.label) {
      const clsLabel = tx("analyze.classification." + cls.label) || cls.label;
      out.push(t("analyze.snapshot.reasoning_classified", { label: clsLabel }));
    }
    const methodNames = methods.map((m) => tx("analyze.snapshot.method_names." + m.name) || m.name);
    if (methodNames.length > 0) {
      out.push(t("analyze.snapshot.reasoning_blended", { methods: methodNames.join(", ") }));
    }
    const up = Number(val.upside_pct);
    if (Number.isFinite(up)) {
      const fvcLabel = val.fair_value_confidence
        ? (tx("analyze.status." + val.fair_value_confidence.toLowerCase()) || val.fair_value_confidence)
        : "";
      out.push(
        t(up >= 0 ? "analyze.snapshot.reasoning_upside" : "analyze.snapshot.reasoning_downside", {
          pct: Math.abs(up).toFixed(1),
          confidence: fvcLabel,
        })
      );
    }
    if (gate.status) {
      const reasonRaw = (gate.reasons && gate.reasons[0]) || "";
      const reasonOut = reasonRaw
        ? (tx("analyze.snapshot.quality_reasons." + reasonRaw) || reasonRaw)
        : t("analyze.snapshot.reasoning_quality_no_flags");
      out.push(
        t("analyze.snapshot.reasoning_quality_gate", {
          status: tx("analyze.status." + gate.status.toLowerCase()) || gate.status,
          reason: reasonOut,
        })
      );
    }
    if (risk.regime) {
      const reasonRaw = (risk.reasons && risk.reasons[0]) || "";
      out.push(
        t("analyze.snapshot.reasoning_risk_regime", {
          regime: tx("analyze.status." + risk.regime.toLowerCase()) || risk.regime,
          reason: reasonRaw,
        })
      );
    }
    if (hardVetoes.length > 0) {
      out.push(t("analyze.snapshot.reasoning_hard_veto", { label: hardVetoes[0].label }));
    } else if (softVetoes.length > 0) {
      out.push(t("analyze.snapshot.reasoning_soft_veto", { label: softVetoes[0].label }));
    }
    if (valueTrap.flag && valueTrap.warning) {
      out.push(t("analyze.snapshot.reasoning_value_trap", { warning: valueTrap.warning }));
    }
    if (confidenceLabel && confidenceLabel !== "High") {
      out.push(
        t("analyze.snapshot.reasoning_confidence_overall", {
          label: tx("analyze.status." + confidenceLabel.toLowerCase()) || confidenceLabel,
          score: confidenceScore ?? "\u2014",
        })
      );
    }
    return out.length > 0 ? out : reasoning;
  })();

  // Quality gate tile colors
  const gateTone =
    gate.status === "Pass"
      ? "good"
      : gate.status === "Watch"
        ? "warn"
        : "bad";
  const GateIcon =
    gate.status === "Pass" ? CheckCircle2 : gate.status === "Watch" ? CircleDot : XCircle;

  const riskTone =
    risk.regime === "Low" ? "good" : risk.regime === "Medium" ? "warn" : "bad";

  const fvTone =
    val.fair_value_confidence === "High"
      ? "good"
      : val.fair_value_confidence === "Medium"
        ? "warn"
        : "bad";

  const confTone =
    confidenceLabel === "High" ? "good" : confidenceLabel === "Medium" ? "warn" : "bad";

  const tiles = [
    {
      icon: Building2,
      label: t("analyze.inst.classification"),
      value: cls.label || "—",
      sub: cls.secondary_label ? `+ ${cls.secondary_label}` : null,
      tone: "neutral",
      tip: {
        title: t("analyze.snapshot.tile_tips.classification_title"),
        body: t("analyze.snapshot.tile_tips.classification_body"),
      },
      testId: "inst-tile-classification",
    },
    {
      icon: GateIcon,
      label: t("analyze.inst.quality_gate"),
      value: gate.status ? (t("analyze.status." + gate.status.toLowerCase()) || gate.status) : "—",
      sub: (gate.reasons && gate.reasons[0]) || null,
      tone: gateTone,
      tip: {
        title: t("analyze.snapshot.tile_tips.gate_title"),
        body: t("analyze.snapshot.tile_tips.gate_body"),
      },
      testId: "inst-tile-gate",
    },
    {
      icon: Gauge,
      label: t("analyze.inst.confidence"),
      value: confidenceLabel ? (t("analyze.status." + confidenceLabel.toLowerCase()) || confidenceLabel) : "—",
      sub: confidenceScore != null ? t("analyze.inst.conviction", { n: confidenceScore }) : null,
      tone: confTone,
      tip: {
        title: t("analyze.snapshot.tile_tips.confidence_title"),
        body: t("analyze.snapshot.tile_tips.confidence_body"),
      },
      testId: "inst-tile-confidence",
    },
    {
      icon: Layers,
      label: t("analyze.inst.methods_used"),
      value: t("analyze.inst.blended", { n: methods.length }),
      sub: methods
        .slice(0, 2)
        .map((m) => m.name)
        .join(", "),
      tone: "neutral",
      tip: {
        title: t("analyze.snapshot.tile_tips.methods_title"),
        body: t("analyze.snapshot.tile_tips.methods_body"),
      },
      testId: "inst-tile-methods",
    },
    {
      icon: AlertTriangle,
      label: t("analyze.inst.risk_regime"),
      value: risk.regime ? (t("analyze.status." + risk.regime.toLowerCase()) || risk.regime) : "—",
      sub: (risk.reasons && risk.reasons[0]) || null,
      tone: riskTone,
      tip: {
        title: t("analyze.snapshot.tile_tips.risk_regime_title"),
        body: t("analyze.snapshot.tile_tips.risk_regime_body"),
      },
      testId: "inst-tile-risk",
    },
    {
      icon: Activity,
      label: t("analyze.inst.fair_value_confidence"),
      value: val.fair_value_confidence ? (t("analyze.status." + val.fair_value_confidence.toLowerCase()) || val.fair_value_confidence) : "—",
      sub: Number.isFinite(Number(val.method_spread_ratio))
        ? `Spread ${safeFixed(Number(val.method_spread_ratio) * 100, 0)}%`
        : null,
      tone: fvTone,
      tip: {
        title: t("analyze.snapshot.tile_tips.fv_confidence_title"),
        body: t("analyze.snapshot.tile_tips.fv_confidence_body"),
      },
      testId: "inst-tile-fv-confidence",
    },
  ];

  // Veto banner styling
  const hasHardVeto = hardVetoes.length > 0;
  const hasSoftVeto = !hasHardVeto && softVetoes.length > 0;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="rounded-2xl border border-border/70 bg-card/60 p-5 sm:p-6 shadow-[var(--app-shadow-elev-1)]"
        data-testid="institutional-snapshot"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#3182CE]" />
            <div className="font-medium">{t("analyze.snapshot.title")}</div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {t("analyze.snapshot.phase_badge")}
            </Badge>
          </div>

          {/* Phase 6.4 — Render the TRUE Fundamental Composite Score
              (price-independent: derived only from Business Quality,
              Growth, and Risk & Safety sub-scores).  Falls back to
              the legacy institutional.composite_score only when the
              new field is not yet on a cached payload. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-xs hover:bg-secondary/70 transition-colors"
                data-testid="inst-composite-score"
              >
                <span className="text-muted-foreground">{t("analyze.snapshot.fundamental_composite")}</span>
                <span className="font-mono tabular font-semibold">
                  {(() => {
                    const truthful = valuation && valuation.fundamental_composite_score;
                    if (truthful != null && Number.isFinite(Number(truthful))) {
                      return `${Math.round(Number(truthful))}/100`;
                    }
                    // Phase 6.4 — do NOT fall back to the old
                    // institutional.composite_score under this label
                    // (that field contains a 35% valuation component
                    // and is not truly price-independent).
                    return "\u2014";
                  })()}
                </span>
                <Info className="w-3 h-3 opacity-50" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="center"
              sideOffset={8}
              collisionPadding={12}
              avoidCollisions
              className="max-w-[320px] text-[12px] leading-[1.55] bg-popover/95 backdrop-blur-xl border border-border/70 shadow-[var(--app-shadow-elev-2)] z-50"
            >
              <div className="font-medium text-foreground mb-1">
                {t("analyze.snapshot.composite_tip_title")}
              </div>
              <div
                className="text-muted-foreground/95"
                dangerouslySetInnerHTML={{ __html: t("analyze.snapshot.composite_tip_intro") }}
              />
              <div className="mt-2 rounded-md border border-border/50 bg-muted/30 p-2 text-[11px] leading-[1.5]">
                <div className="font-semibold text-foreground mb-1">{t("analyze.snapshot.composite_relation_title")}</div>
                <ul className="space-y-1 text-muted-foreground/95">
                  <li dangerouslySetInnerHTML={{ __html: t("analyze.snapshot.composite_relation_ai") }} />
                  <li dangerouslySetInnerHTML={{ __html: t("analyze.snapshot.composite_relation_fc") }} />
                </ul>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground/90">
                {t("analyze.snapshot.weights_label")}{" "}
                <span className="text-foreground">{t("analyze.snapshot.weight_valuation")}</span>,{" "}
                <span className="text-foreground">{t("analyze.snapshot.weight_quality")}</span>,{" "}
                <span className="text-foreground">{t("analyze.snapshot.weight_growth")}</span>,{" "}
                <span className="text-foreground">{t("analyze.snapshot.weight_risk")}</span>.
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                <span className="text-muted-foreground">{t("analyze.snapshot.breakdown_valuation")}</span>
                <span className="font-mono text-right">{breakdown.valuation ?? "–"}</span>
                <span className="text-muted-foreground">{t("analyze.snapshot.breakdown_quality")}</span>
                <span className="font-mono text-right">{breakdown.quality ?? "–"}</span>
                <span className="text-muted-foreground">{t("analyze.snapshot.breakdown_growth")}</span>
                <span className="font-mono text-right">{breakdown.growth ?? "–"}</span>
                <span className="text-muted-foreground">{t("analyze.snapshot.breakdown_risk")}</span>
                <span className="font-mono text-right">{breakdown.risk ?? "–"}</span>
                {typeof breakdown.trend_modifier === "number" ? (
                  <>
                    <span className="text-muted-foreground">{t("analyze.snapshot.breakdown_trend")}</span>
                    <span className="font-mono text-right">
                      {breakdown.trend_modifier > 0 ? "+" : ""}
                      {breakdown.trend_modifier}
                    </span>
                  </>
                ) : null}
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        <p className="text-xs text-muted-foreground mt-1.5">
          {t("analyze.snapshot.subtitle")}
          <span className="ml-1 text-muted-foreground/70">
            {t("analyze.snapshot.tap_to_learn")}
          </span>
        </p>

        {/* Veto banner — HARD veto takes precedence over soft */}
        {hasHardVeto ? (
          <VetoBanner kind="hard" items={hardVetoes} t={t} />
        ) : hasSoftVeto ? (
          <VetoBanner kind="soft" items={softVetoes} t={t} />
        ) : null}

        {/* Value-trap banner (only when no hard veto already covers it) */}
        {valueTrap.flag && !hasHardVeto ? (
          <ValueTrapBanner valueTrap={valueTrap} t={t} />
        ) : null}

        {/* Tile grid */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {tiles.map((tile, i) => (
            <SnapshotTile key={i} {...tile} />
          ))}
        </div>

        {/* Detected risks — premium compact list (top 4) */}
        {detectedRisks.length > 0 ? (
          <DetectedRisksList risks={detectedRisks} t={t} />
        ) : null}

        {/* Reasoning bullets */}
        {localizedReasoning.length > 0 ? (
          <div
            className="mt-4 rounded-xl border border-border/60 bg-secondary/20 px-3 py-2.5"
            data-testid="inst-reasoning"
          >
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
              {t("analyze.inst.how_we_got_there")}
            </div>
            <ul className="space-y-1 text-[12.5px] leading-snug">
              {localizedReasoning.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#3182CE] mt-[2px]">•</span>
                  <span className="text-muted-foreground/95">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Methods used — premium pills.
            Mobile: 2-col grid so pills always align cleanly and pair up
            evenly with consistent rhythm.
            Desktop: flex-wrap with breathing room. */}
        {methods.length > 0 ? (
          <div className="mt-4" data-testid="inst-methods-strip">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-3.5 h-3.5 text-[#6FC9D5]" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t("analyze.snapshot.methods_used_label")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2">
              {methods.map((m, i) => (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="w-full sm:w-auto inline-flex items-center justify-between sm:justify-start gap-2 rounded-xl border border-border/70 bg-card/40 px-3 py-2 sm:px-2.5 sm:py-1 text-[12px] sm:text-[11px] hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)] transition-colors"
                      data-testid={`inst-method-${i}`}
                    >
                      <span className="font-medium truncate">{m.name}</span>
                      <span className="text-muted-foreground tabular shrink-0">
                        {safeRatioPct(m.weight, 0)}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={6}
                    className="max-w-[260px] text-[11.5px] leading-[1.5] bg-popover/95 backdrop-blur-xl border border-border/70"
                  >
                    <div className="font-medium text-foreground mb-0.5">{m.name}</div>
                    <div className="text-muted-foreground/95">{m.notes || "—"}</div>
                    <div className="mt-1 text-[10.5px] text-muted-foreground/80">
                      {t("analyze.snapshot.method_fair_value")}: <span className="font-mono text-foreground">{Number.isFinite(Number(m.fair_value)) ? `$${safeFixed(m.fair_value, 2)}` : "—"}</span>
                      {"  ·  "}
                      {t("analyze.snapshot.method_weight")}: <span className="font-mono text-foreground">{safeRatioPct(m.weight, 0)}</span>
                      {"  ·  "}
                      {t("analyze.snapshot.method_confidence")}: <span className="font-mono text-foreground">{safeRatioPct(m.confidence, 0)}</span>
                    </div>
                    {m.proxied_inputs && m.proxied_inputs.length > 0 ? (
                      <div className="mt-1 text-[10.5px] text-[#E0B341]">
                        {t("analyze.snapshot.method_proxied")} {m.proxied_inputs.join(", ")}
                      </div>
                    ) : null}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

// ---------- Subcomponents ----------

function VetoBanner({ kind, items, t }) {
  const hard = kind === "hard";
  const color = hard
    ? { fg: "#E5484D", bg: "rgba(229,72,77,0.12)", border: "rgba(229,72,77,0.35)" }
    : { fg: "#E0B341", bg: "rgba(224,179,65,0.10)", border: "rgba(224,179,65,0.30)" };
  return (
    <div
      className="mt-3 rounded-xl border px-3 py-2.5 flex items-start gap-3"
      style={{ background: color.bg, borderColor: color.border }}
      data-testid={hard ? "inst-hard-veto-banner" : "inst-soft-veto-banner"}
    >
      <span
        className="inline-flex w-8 h-8 items-center justify-center rounded-md shrink-0 mt-0.5"
        style={{ background: hard ? "rgba(229,72,77,0.20)" : "rgba(224,179,65,0.20)", color: color.fg }}
      >
        <ShieldAlert className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: color.fg }}>
            {hard ? t("analyze.snapshot.veto_hard_label") : t("analyze.snapshot.veto_soft_label")}
          </span>
          <Badge
            variant="outline"
            className="text-[10px]"
            style={{ color: color.fg, borderColor: color.border }}
          >
            {hard ? t("analyze.snapshot.veto_hard_badge") : t("analyze.snapshot.veto_soft_badge")}
          </Badge>
        </div>
        <ul className="mt-1 space-y-0.5 text-[12.5px] text-muted-foreground/95">
          {items.slice(0, 3).map((v, i) => (
            <li key={i} className="flex gap-2">
              <span style={{ color: color.fg }} className="mt-[2px]">•</span>
              <span>
                <span className="font-medium text-foreground">{v.label}.</span>{" "}
                {v.evidence}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ValueTrapBanner({ valueTrap, t }) {
  const color = { fg: "#E0B341", bg: "rgba(224,179,65,0.10)", border: "rgba(224,179,65,0.30)" };
  return (
    <div
      className="mt-3 rounded-xl border px-3 py-2.5 flex items-start gap-3"
      style={{ background: color.bg, borderColor: color.border }}
      data-testid="inst-value-trap-banner"
    >
      <span
        className="inline-flex w-8 h-8 items-center justify-center rounded-md shrink-0 mt-0.5"
        style={{ background: "rgba(224,179,65,0.20)", color: color.fg }}
      >
        <TrendingDown className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: color.fg }}>
            {t("analyze.snapshot.value_trap_label")} · {valueTrap.severity}
          </span>
        </div>
        <p className="mt-1 text-[12.5px] text-muted-foreground/95">
          {valueTrap.warning}{" "}
          {valueTrap.reasons && valueTrap.reasons.length > 0 ? (
            <span className="text-muted-foreground/80">
              ({valueTrap.reasons.join("; ")})
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

const RISK_CATEGORY_KEYS = {
  valuation: "valuation",
  balance_sheet: "balance_sheet",
  operational: "operational",
  capital_structure: "capital_structure",
  external: "external",
};

function DetectedRisksList({ risks, t }) {
  if (!risks.length) return null;
  return (
    <div className="mt-4" data-testid="inst-detected-risks">
      <div className="flex items-center gap-2 mb-2">
        <Bug className="w-3.5 h-3.5 text-[#E0B341]" />
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {t("analyze.snapshot.detected_risks")}
        </span>
      </div>
      <ul className="space-y-2 sm:space-y-1.5">
        {risks.map((r, i) => (
          <RiskRow key={i} risk={r} testId={`inst-risk-${i}`} t={t} />
        ))}
      </ul>
    </div>
  );
}

function RiskRow({ risk, testId, t }) {
  const sev = risk.severity || "medium";
  const color =
    sev === "high"
      ? { fg: "#E5484D", border: "rgba(229,72,77,0.32)", bg: "rgba(229,72,77,0.08)" }
      : sev === "medium"
        ? { fg: "#E0B341", border: "rgba(224,179,65,0.30)", bg: "rgba(224,179,65,0.06)" }
        : { fg: "#A8AEB7", border: "rgba(168,174,183,0.25)", bg: "rgba(168,174,183,0.05)" };
  const catKey = RISK_CATEGORY_KEYS[risk.category] || "other";
  const catLabel = t("analyze.snapshot.risk_category." + catKey);
  // Evidence is now shown inline on every breakpoint — never truncated or
  // hidden behind a tooltip. Wraps naturally; mobile gets larger padding
  // and rhythm, desktop stays a touch tighter for density.
  return (
    <li
      data-testid={testId}
      className="rounded-xl border px-3 py-2.5 sm:px-3 sm:py-2 transition-colors"
      style={{ borderColor: color.border, background: color.bg }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className="inline-flex w-1.5 h-1.5 rounded-full mt-[7px] shrink-0"
          style={{ background: color.fg }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] sm:text-[12.5px] font-medium text-foreground leading-snug break-words">
              {risk.label}
            </span>
            <Badge
              variant="outline"
              className="text-[9.5px] uppercase tracking-wider shrink-0"
              style={{ color: color.fg, borderColor: color.border }}
            >
              {catLabel} · {sev}
            </Badge>
          </div>
          {risk.evidence ? (
            <p className="mt-1.5 sm:mt-1 text-[12.5px] sm:text-[12px] leading-snug text-muted-foreground/95 break-words">
              {risk.evidence}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function SnapshotTile({ icon: Icon, label, value, sub, tone, tip, testId }) {
  // Phase 7.UI.8 — `neutral` (info/classification/methods) tiles now use
  // the brand blue family instead of weak grey so they don't look like
  // disabled/unavailable data. Grey is reserved for truly locked or
  // missing values, which use the KpiCard `locked` path elsewhere.
  const colors =
    tone === "good"
      ? { fg: "#3FAE89", bg: "rgba(63,174,137,0.10)", border: "rgba(63,174,137,0.30)" }
      : tone === "bad"
        ? { fg: "#E5484D", bg: "rgba(229,72,77,0.10)", border: "rgba(229,72,77,0.28)" }
        : tone === "warn"
          ? { fg: "#E0B341", bg: "rgba(224,179,65,0.10)", border: "rgba(224,179,65,0.30)" }
          : { fg: "#5B8DC4", bg: "rgba(49,130,206,0.10)", border: "rgba(49,130,206,0.32)" };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="group w-full text-left rounded-xl px-3 py-2.5 border bg-card/40 flex items-start gap-3 hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)] transition-colors"
          style={{ borderColor: colors.border }}
          aria-label={`${label}: ${value}. Tap for explanation.`}
        >
          <span
            className="inline-flex w-7 h-7 items-center justify-center rounded-md shrink-0 mt-0.5"
            style={{ background: colors.bg, color: colors.fg }}
          >
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground truncate flex items-center gap-1">
              {label}
              <Info className="w-2.5 h-2.5 opacity-50 group-hover:opacity-100 transition-opacity" aria-hidden />
            </div>
            <div className="font-medium text-sm truncate" style={{ color: colors.fg }}>
              {value}
            </div>
            {sub ? (
              <div className="text-[10.5px] text-muted-foreground/80 truncate mt-0.5">
                {sub}
              </div>
            ) : null}
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        avoidCollisions
        className="max-w-[320px] text-[12px] leading-[1.55] bg-popover/95 backdrop-blur-xl border border-border/70 shadow-[var(--app-shadow-elev-2)] z-50"
      >
        <div className="font-medium text-foreground mb-1">{tip.title}</div>
        <div
          className="text-muted-foreground/95"
          dangerouslySetInnerHTML={{ __html: tip.body }}
        />
      </TooltipContent>
    </Tooltip>
  );
}
